import { useState } from 'react';
import type { Project } from '@faktorplus/shared';
import { createProjectSchema } from '@faktorplus/shared';
import {
  useCreateProject,
  useProjects,
  useSiteManagers,
  useUpdateProject,
} from '../../api/dictionaries';
import { Button } from '../../components/ui/button';
import { PencilIcon } from '../../components/ui/icons';
import { Field, Input } from '../../components/ui/form';
import { Modal, modalStyles } from '../../components/ui/modal';
import { EmptyState, ErrorBox, LoadingBlock } from '../../components/ui/feedback';
import { ApiError } from '../../lib/api-client';
import { formatMoney } from '../../lib/format';
import pageStyles from '../../components/layout/page.module.css';
import styles from './settings-page.module.css';

export function ProjectsTab() {
  const { data: projects = [], isLoading, error } = useProjects(true);
  const [editing, setEditing] = useState<Project | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorBox>Nie udało się pobrać listy budów.</ErrorBox>;

  return (
    <>
      <div className={pageStyles.header}>
        <span className={pageStyles.subtitle}>
          {projects.filter((project) => project.isActive).length} aktywnych z {projects.length}
        </span>
        <div className={pageStyles.actions}>
          <Button variant="primary" onClick={() => setIsCreating(true)}>
            + Nowa budowa
          </Button>
        </div>
      </div>

      {projects.length === 0 ? (
        <EmptyState mark="∅" title="Brak budów" description="Dodaj pierwszą budowę." />
      ) : (
        <div className={pageStyles.tableWrap}>
          <table className={pageStyles.table}>
            <thead>
              <tr>
                <th>Kod</th>
                <th>Nazwa</th>
                <th>Kierownicy</th>
                <th className={pageStyles.numeric}>Wartość kontraktu</th>
                <th>Stan</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => (
                <ProjectRow key={project.id} project={project} onEdit={() => setEditing(project)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(isCreating || editing) && (
        <ProjectDialog
          project={editing}
          onClose={() => {
            setIsCreating(false);
            setEditing(null);
          }}
        />
      )}
    </>
  );
}

function ProjectRow({ project, onEdit }: { project: Project; onEdit: () => void }) {
  const { data: managers = [] } = useSiteManagers();

  const assigned = managers
    .filter((manager) => project.managerIds.includes(manager.id))
    .map((manager) => manager.fullName);

  return (
    <tr className={project.isActive ? undefined : styles.inactive}>
      <td className={pageStyles.docNumber}>{project.code}</td>
      <td className={pageStyles.strong}>{project.name}</td>
      <td className={assigned.length ? undefined : pageStyles.muted}>
        {assigned.length ? assigned.join(', ') : 'brak'}
      </td>
      <td className={pageStyles.numeric}>
        {project.contractValue === null ? (
          <span className={pageStyles.muted}>—</span>
        ) : (
          formatMoney(project.contractValue)
        )}
      </td>
      <td>
        <span className={`${styles.chip} ${project.isActive ? styles.chipAccent : ''}`}>
          {project.isActive ? 'Aktywna' : 'Zakończona'}
        </span>
      </td>
      <td>
        <div className={styles.rowActions}>
          <Button variant="soft" icon={<PencilIcon />} onClick={onEdit}>
            Edytuj
          </Button>
        </div>
      </td>
    </tr>
  );
}

function ProjectDialog({ project, onClose }: { project: Project | null; onClose: () => void }) {
  const { data: managers = [] } = useSiteManagers();
  const create = useCreateProject();
  const update = useUpdateProject();

  const [code, setCode] = useState(project?.code ?? '');
  const [name, setName] = useState(project?.name ?? '');
  const [contractValue, setContractValue] = useState(
    project?.contractValue != null ? String(project.contractValue) : '',
  );
  const [managerIds, setManagerIds] = useState<string[]>(project?.managerIds ?? []);
  const [isActive, setIsActive] = useState(project?.isActive ?? true);
  const [validationError, setValidationError] = useState<string | null>(null);

  const isPending = create.isPending || update.isPending;
  const serverError = create.error ?? update.error;

  function toggleManager(id: string) {
    setManagerIds((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    );
  }

  async function handleSubmit() {
    setValidationError(null);

    const payload = {
      code: code.trim().toUpperCase(),
      name: name.trim(),
      contractValue: contractValue ? Number(contractValue) : null,
      managerIds,
    };

    const parsed = createProjectSchema.safeParse(payload);
    if (!parsed.success) {
      setValidationError(parsed.error.issues[0]?.message ?? 'Sprawdź wprowadzone dane');
      return;
    }

    if (project) {
      await update.mutateAsync({ id: project.id, input: { ...parsed.data, isActive } });
    } else {
      await create.mutateAsync(parsed.data);
    }
    onClose();
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={project ? 'Edytuj budowę' : 'Nowa budowa'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            Anuluj
          </Button>
          <span className={modalStyles.footerSpacer} />
          <Button variant="primary" isLoading={isPending} onClick={() => void handleSubmit()}>
            {project ? 'Zapisz zmiany' : 'Dodaj budowę'}
          </Button>
        </>
      }
    >
      <div className={modalStyles.row}>
        <Field label="Kod">
          {(id) => (
            <Input
              id={id}
              value={code}
              placeholder="MROZNA"
              autoFocus={!project}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
            />
          )}
        </Field>

        <Field label="Wartość kontraktu (netto)">
          {(id) => (
            <Input
              id={id}
              type="number"
              min="0"
              step="0.01"
              value={contractValue}
              placeholder="opcjonalnie"
              onChange={(event) => setContractValue(event.target.value)}
            />
          )}
        </Field>
      </div>

      <Field label="Nazwa">
        {(id) => (
          <Input
            id={id}
            value={name}
            placeholder="Mroźna"
            onChange={(event) => setName(event.target.value)}
          />
        )}
      </Field>

      <Field label="Kierownicy budowy">
        {() =>
          managers.length === 0 ? (
            <p className={pageStyles.muted}>Najpierw dodaj użytkowników z rolą kierownika.</p>
          ) : (
            <div className={modalStyles.checkList}>
              {managers.map((manager) => (
                <label key={manager.id} className={modalStyles.checkItem}>
                  <input
                    type="checkbox"
                    className={pageStyles.checkbox}
                    checked={managerIds.includes(manager.id)}
                    onChange={() => toggleManager(manager.id)}
                  />
                  {manager.fullName}
                </label>
              ))}
            </div>
          )
        }
      </Field>

      {project && (
        <label className={modalStyles.checkItem}>
          <input
            type="checkbox"
            className={pageStyles.checkbox}
            checked={isActive}
            onChange={(event) => setIsActive(event.target.checked)}
          />
          Budowa aktywna
        </label>
      )}

      {validationError && <ErrorBox>{validationError}</ErrorBox>}
      {serverError && (
        <ErrorBox>
          {serverError instanceof ApiError ? serverError.message : 'Nie udało się zapisać.'}
        </ErrorBox>
      )}
    </Modal>
  );
}
